/**
 * 전역 메뉴 권한 플러그인
 * 모든 컴포넌트 템플릿에서 $auth.canCreate, $auth.canUpdate, $auth.canDelete,
 * $auth.canExcelDown, $auth.canExcelUp 으로 현재 라우트의 CRUD 권한 확인 가능
 *
 * 사용법 (template):
 *   <button v-if="$auth.canCreate" @click="onCreate">등록</button>
 *   <button v-if="$auth.canUpdate" @click="onEdit">수정</button>
 *   <button v-if="$auth.canDelete" @click="onDelete">삭제</button>
 *   <button v-if="$auth.canExcelDown" @click="download">엑셀</button>
 *   <button v-if="$auth.canExcelUp"  @click="upload">업로드</button>
 */
import { reactive } from 'vue';
import axios from 'axios';

function resolveContextPath() {
  const marker = '/app/';
  const path = window.location.pathname;
  const idx = path.indexOf(marker);
  return idx > 0 ? path.substring(0, idx) : '';
}

/** 서브라우트 → 메뉴 경로 매핑 (메뉴 트리에 없는 하위 라우트) */
const SUB_ROUTE_MAP = {
  '/investor/spc-detail': '/investor/spc-list',
  '/investor/spc-create': '/investor/spc-list',
  '/investor/investor-detail': '/investor/investor-list',
  '/schedule/detail': '/schedule/list',
  '/loan/app-detail': '/loan/app-list',
  '/loan/create': '/loan/lontmlf',
  '/loan/detail': '/loan/LoanList',
};

export default {
  install(app, { router }) {
    // 내부 상태 (비공개)
    const _state = {
      menuAuthMap: {},
      loaded: false,
      userRole: '',
      currentPath: '',
    };

    // 전역 반응형 권한 객체
    const auth = reactive({
      canCreate: true,
      canRead: true,
      canUpdate: true,
      canDelete: true,
      canExcelDown: true,
      canExcelUp: true,
      isMaster: false,
      loaded: false,
      menuList: [],       // 사이드바용 메뉴 트리 (서버가 권한 필터링해서 반환)
      comcode: '',        // 현재 로그인 사용자 기관코드
      comName: '',        // 현재 로그인 사용자 기관명
      role: '',           // 현재 로그인 사용자 역할
      loginId: '',        // 현재 로그인 사용자 ID (OPERATOR 자기자신 식별용)
    });

    function getMenuPath(routePath) {
      return SUB_ROUTE_MAP[routePath] || routePath;
    }

    function updateAuth() {
      if (!_state.loaded) {
        // 로드 전: 기본 허용 (화면 깜빡임 방지)
        auth.canCreate = true;
        auth.canRead = true;
        auth.canUpdate = true;
        auth.canDelete = true;
        auth.canExcelDown = true;
        auth.canExcelUp = true;
        auth.isMaster = false;
        auth.loaded = false;
        return;
      }

      auth.loaded = true;
      auth.isMaster = _state.userRole === 'MASTER';

      // MASTER: 모든 권한
      if (_state.userRole === 'MASTER') {
        auth.canCreate = true;
        auth.canRead = true;
        auth.canUpdate = true;
        auth.canDelete = true;
        auth.canExcelDown = true;
        auth.canExcelUp = true;
        return;
      }

      const menuPath = getMenuPath(_state.currentPath);
      const a = _state.menuAuthMap[menuPath] || {};
      auth.canCreate = a.authCreate === 'Y';
      auth.canRead = a.authRead === 'Y';
      auth.canUpdate = a.authUpdate === 'Y';
      auth.canDelete = a.authDelete === 'Y';
      auth.canExcelDown = a.authExcelDownload === 'Y';
      auth.canExcelUp = a.authExcelUpload === 'Y';
    }

    async function loadAuthInfo() {
      try {
        const ctx = resolveContextPath();
        const res = await axios.post(`${ctx}/api/auth/me`, {}, { withCredentials: true });
        const data = res.data;
        _state.userRole = data.role || '';

        const map = {};
        const collect = (menus) => {
          for (const m of (menus || [])) {
            if (m.menuPath) {
              map[m.menuPath] = {
                authCreate: m.authCreate || 'N',
                authRead: m.authRead || 'N',
                authUpdate: m.authUpdate || 'N',
                authDelete: m.authDelete || 'N',
                authExcelDownload: m.authExcelDownload || 'N',
                authExcelUpload: m.authExcelUpload || 'N',
              };
            }
            if (m.children) collect(m.children);
          }
        };
        collect(data.menuList || []);
        _state.menuAuthMap = map;
        _state.loaded = true;
        updateAuth();

        // 사이드바용 메뉴 트리 노출 (MASTER는 전체, 그 외는 authRead=Y 항목만)
        // 서버 getMenuTreeWithAuth가 이미 권한 필터링된 목록을 반환
        auth.menuList = data.menuList || [];
        auth.comcode = data.comcode || '';
        auth.comName = data.comName || '';
        auth.role = data.role || '';
        auth.loginId = data.username || '';  // MeResp.username = 세션 LOGIN_ID
      } catch (e) {
        // 로그인 안 된 경우 기본값 유지
      }
    }

    // 라우트 변경 시 권한 업데이트
    router.afterEach((to) => {
      _state.currentPath = to.path;
      updateAuth();
    });

    // 초기 라우트 설정
    _state.currentPath = router.currentRoute?.value?.path || '/';

    // 인증 정보 로드
    router.isReady().then(loadAuthInfo);

    // 메뉴 권한 저장 후 즉시 반영: AdminAuthorization 저장 시 이벤트 수신하여 재로드
    window.addEventListener('menu-auth-updated', () => {
      loadAuthInfo();
    });

    // 전역 프로퍼티 등록 (Options API / Template 접근)
    app.config.globalProperties.$auth = auth;

    // provide 등록 (Composition API inject 지원)
    app.provide('auth', auth);
  }
};
